import React, { useState, useEffect, useMemo } from 'react';
import TermsOfService from './TermsOfService';
import './RegisterPage.css';

interface RegisterPageProps {
  onSwitchToLogin: () => void;
}

interface TerminalLine {
  text: string | React.ReactNode;
  type: 'comment' | 'code' | 'success' | 'error' | 'output';
  isTyping?: boolean;
  id?: string;
}

// Helper to create syntax-highlighted Lua code
const highlightLua = (code: string, isTyping?: boolean): React.ReactNode => {
  // Find all matches
  interface Match {
    index: number;
    text: string;
    className: string;
  }
  const matches: Match[] = [];
  
  const keywordRegex = /\b(local|require|function|end|return|if|then|else|elseif|for|while|do|nil|true|false)\b/g;
  const stringRegex = /"(?:[^"\\]|\\.)*"/g;
  
  let match;
  while ((match = keywordRegex.exec(code)) !== null) {
    matches.push({ index: match.index, text: match[0], className: 'lua-keyword' });
  }
  while ((match = stringRegex.exec(code)) !== null) {
    matches.push({ index: match.index, text: match[0], className: 'lua-string' });
  }
  
  // Sort by index
  matches.sort((a, b) => a.index - b.index);
  
  // Build parts
  const parts: { text: string; className?: string }[] = [];
  let currentIndex = 0;
  for (const m of matches) {
    if (m.index > currentIndex) {
      parts.push({ text: code.slice(currentIndex, m.index) });
    }
    parts.push({ text: m.text, className: m.className });
    currentIndex = m.index + m.text.length;
  }
  if (currentIndex < code.length) {
    parts.push({ text: code.slice(currentIndex) });
  }
  
  return (
    <>
      {parts.map((part, i) => (
        part.className ? (
          <span key={i} className={part.className}>{part.text}</span>
        ) : (
          <span key={i}>{part.text}</span>
        )
      ))}
      {isTyping && <span className="terminal-cursor">_</span>}
    </>
  );
};

const RegisterPage: React.FC<RegisterPageProps> = ({ onSwitchToLogin }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [invite, setInvite] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showTOS, setShowTOS] = useState(false);
  
  // Static/historical lines that don't change
  const [historyLines, setHistoryLines] = useState<TerminalLine[]>([
    { text: '-- Volt Registration Module v1.1.0', type: 'comment' },
    { text: 'local VoltAuth = require("@volt/auth")', type: 'code' },
    { text: 'local registration = {}', type: 'code' },
    { text: '', type: 'code' },
  ]);
  
  const [authResult, setAuthResult] = useState<'success' | 'error' | null>(null);
  const [typingUsername, setTypingUsername] = useState('');
  const [typingEmail, setTypingEmail] = useState('');
  const [typingPassword, setTypingPassword] = useState('');
  const [typingInvite, setTypingInvite] = useState('');
  const [focusedInput, setFocusedInput] = useState<'username' | 'email' | 'password' | 'invite' | null>(null);

  // Typing effects
  useEffect(() => {
    if (username.length > typingUsername.length) {
      const timeout = setTimeout(() => {
        setTypingUsername(username.slice(0, typingUsername.length + 1));
      }, 30);
      return () => clearTimeout(timeout);
    } else if (username.length < typingUsername.length) {
      setTypingUsername(username);
    }
  }, [username, typingUsername]);

  useEffect(() => {
    if (email.length > typingEmail.length) {
      const timeout = setTimeout(() => {
        setTypingEmail(email.slice(0, typingEmail.length + 1));
      }, 30);
      return () => clearTimeout(timeout);
    } else if (email.length < typingEmail.length) {
      setTypingEmail(email);
    }
  }, [email, typingEmail]);

  useEffect(() => {
    if (password.length > typingPassword.length) {
      const timeout = setTimeout(() => {
        setTypingPassword(password.slice(0, typingPassword.length + 1));
      }, 30);
      return () => clearTimeout(timeout);
    } else if (password.length < typingPassword.length) {
      setTypingPassword(password);
    }
  }, [password, typingPassword]);

  useEffect(() => {
    if (invite.length > typingInvite.length) {
      const timeout = setTimeout(() => {
        setTypingInvite(invite.slice(0, typingInvite.length + 1));
      }, 30);
      return () => clearTimeout(timeout);
    } else if (invite.length < typingInvite.length) {
      setTypingInvite(invite);
    }
  }, [invite, typingInvite]);

  // Compute terminal lines: history + live registration lines
  const terminalLines = useMemo(() => {
    const lines: TerminalLine[] = [...historyLines];
    
    if (typingUsername) {
      lines.push({
        text: `registration.username = "${typingUsername}"`,
        type: 'code',
        isTyping: focusedInput === 'username',
        id: 'username-line'
      });
    }
    
    if (typingEmail) {
      lines.push({
        text: `registration.email = "${typingEmail}"`,
        type: 'code',
        isTyping: focusedInput === 'email',
        id: 'email-line'
      });
    }
    
    if (typingPassword) {
      const masked = '*'.repeat(typingPassword.length);
      lines.push({
        text: `registration.password = "${masked}"`,
        type: 'code',
        isTyping: focusedInput === 'password',
        id: 'password-line'
      });
    }
    
    if (typingInvite) {
      lines.push({
        text: `registration.license = "${typingInvite}"`,
        type: 'code',
        isTyping: focusedInput === 'invite',
        id: 'invite-line'
      });
    }
    
    return lines;
  }, [historyLines, typingUsername, typingEmail, typingPassword, typingInvite, focusedInput]);

  const addTerminalLine = (line: TerminalLine) => {
    setHistoryLines(prev => [...prev, line]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Custom validation
    if (!username.trim()) {
      setValidationError('Please enter a username');
      return;
    }
    if (!email.trim()) {
      setValidationError('Please enter your email');
      return;
    }
    if (!email.includes('@')) {
      setValidationError('Please enter a valid email address');
      return;
    }
    if (!password) {
      setValidationError('Please enter a password');
      return;
    }
    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return;
    }
    if (!invite.trim()) {
      setValidationError('Please enter a license key');
      return;
    }
    
    // Save values before clearing
    const savedUsername = username;
    const savedEmail = email;
    const savedPassword = password;
    const savedInvite = invite;
    
    setIsLoading(true);
    setAuthResult(null);
    setValidationError(null);
    
    // Freeze the current data into history before showing register attempt
    if (typingUsername) {
      addTerminalLine({ text: `registration.username = "${typingUsername}"`, type: 'code' });
    }
    if (typingEmail) {
      addTerminalLine({ text: `registration.email = "${typingEmail}"`, type: 'code' });
    }
    if (typingPassword) {
      const masked = '*'.repeat(typingPassword.length);
      addTerminalLine({ text: `registration.password = "${masked}"`, type: 'code' });
    }
    if (typingInvite) {
      addTerminalLine({ text: `registration.invite = "${typingInvite}"`, type: 'code' });
    }
    
    addTerminalLine({ text: '', type: 'code' });
    addTerminalLine({ text: '-- Initiating registration...', type: 'comment' });
    addTerminalLine({ text: 'local response = VoltAuth:Register(registration)', type: 'code' });
    
    // Clear input fields now that they're in history
    setUsername('');
    setEmail('');
    setPassword('');
    setInvite('');
    setTypingUsername('');
    setTypingEmail('');
    setTypingPassword('');
    setTypingInvite('');
    
    try {
      const response = await fetch('https://api.volt.bz/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username: savedUsername, 
          email: savedEmail, 
          password: savedPassword, 
          license: savedInvite 
        }),
      });
      
      const data = await response.json();
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (response.ok) {
        addTerminalLine({ text: '', type: 'code' });
        addTerminalLine({ text: 'print(response.status)', type: 'code' });
        addTerminalLine({ text: '>> SUCCESS', type: 'success' });
        addTerminalLine({ text: '-- Account created. Redirecting to login...', type: 'comment' });
        setAuthResult('success');
        
        // Redirect to login after success
        setTimeout(() => {
          onSwitchToLogin();
        }, 2000);
      } else {
        addTerminalLine({ text: '', type: 'code' });
        addTerminalLine({ text: 'print(response.error)', type: 'code' });
        addTerminalLine({ text: `>> ERROR: ${data.message || 'Registration failed'}`, type: 'error' });
        addTerminalLine({ text: '-- Registration failed. Check your details.', type: 'comment' });
        addTerminalLine({ text: '', type: 'code' });
        addTerminalLine({ text: 'registration = {}', type: 'code' });
        setAuthResult('error');
      }
    } catch (error) {
      addTerminalLine({ text: '', type: 'code' });
      addTerminalLine({ text: '>> ERROR: Connection failed', type: 'error' });
      addTerminalLine({ text: '-- Unable to reach registration server.', type: 'comment' });
      addTerminalLine({ text: '', type: 'code' });
      addTerminalLine({ text: 'registration = {}', type: 'code' });
      setAuthResult('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-layout">
        {/* Left side - Register Form */}
        <div className="register-container">
          <div className="register-card">
            <div className="register-header">
              <img src="/bolt-full.svg" alt="Volt Logo" className="register-logo" />
              <h1 className="register-title">Create Account</h1>
              <p className="register-subtitle">Join Volt with a license key.</p>
            </div>

            <form className="register-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  className="form-input"
                  placeholder="johndoe"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setValidationError(null); }}
                  onFocus={() => setFocusedInput('username')}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="text"
                  id="email"
                  className="form-input"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setValidationError(null); }}
                  onFocus={() => setFocusedInput('email')}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setValidationError(null); }}
                  onFocus={() => setFocusedInput('password')}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="invite" className="form-label">
                  License Key
                </label>
                <input
                  type="text"
                  id="invite"
                  className="form-input"
                  placeholder="VOLT-XXXX-XXXX"
                  value={invite}
                  onChange={(e) => { setInvite(e.target.value); setValidationError(null); }}
                  onFocus={() => setFocusedInput('invite')}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>

              {validationError && (
                <div className="validation-error">
                  {validationError}
                </div>
              )}

              <button 
                type="submit" 
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="btn-loading">
                    <span className="btn-spinner"></span>
                    Creating account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>

              <div className="divider">
                <span>OR</span>
              </div>

              <button type="button" className="signin-btn" onClick={onSwitchToLogin}>
                Sign in instead
              </button>
            </form>

            <p className="terms-text">
              By continuing, you agree to our{' '}
              <button type="button" className="terms-link-btn" onClick={() => setShowTOS(true)}>Terms of Service</button>
              {' '}and{' '}
              <button type="button" className="terms-link-btn" onClick={() => setShowTOS(true)}>Privacy Policy</button>.
            </p>
          </div>
        </div>

        {/* Right side - Terminal */}
        <div className="terminal-container">
          <div className="terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
              </div>
              <span className="terminal-title">register.lua</span>
            </div>
            <div className="terminal-body">
              {terminalLines.map((line, index) => (
                <div key={line.id || index} className={`terminal-line ${line.type}`}>
                  {line.type === 'output' || line.type === 'success' || line.type === 'error' ? (
                    <span>{typeof line.text === 'string' ? line.text : line.text}</span>
                  ) : (
                    <>
                      <span className="line-number">{index + 1}</span>
                      <span className="line-content">
                        {line.type === 'code' && typeof line.text === 'string' 
                          ? highlightLua(line.text, line.isTyping)
                          : (
                            <>
                              {line.text}
                              {line.isTyping && <span className="terminal-cursor">_</span>}
                            </>
                          )
                        }
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Terms of Service Modal */}
      {showTOS && <TermsOfService onClose={() => setShowTOS(false)} />}
    </div>
  );
};

export default RegisterPage;
